/**
 * CREATE / ALTER / DROP {FUNCTION|PROCEDURE} structure extractor — the
 * grammar-backed replacement for the removed regex parked-routine parser
 * (VDE-2 Slice 4). Pure, regex-free: every structural decision comes from
 * character-level keyword / phrase consumption plus the bespoke
 * `routine.parse.ts` helpers — never regex (project rule VQE-D10), and
 * ABSOLUTELY never regex into the routine body or the dollar-quote span (the
 * entire point of the slice). It deliberately does NOT use the shared
 * `readIdentifier`/`findMatchingParen` primitives: those are comment- and
 * engine-aware and would change behaviour vs the removed parser, breaking the
 * required byte-parity (the removed parser had its OWN comment-deaf local
 * `findMatchingParen`/`splitTopLevelCommas`). Low-level lexical helpers live
 * in `routine.parse.ts`; this module is the clause orchestration + entry
 * points.
 *
 * ROUTINE is **seed-only** AND the second **body-bearing** shell (after
 * TRIGGER): the inline `[Edit Routine]` popover builds SQL from form values
 * and replaces the statement text. There is no structural apply path, so the
 * arm carries the pre-parsed seed (`parsed`) plus the statement range. The
 * procedural body is carried as ONE OPAQUE verbatim string (`bodyText`) —
 * never parsed, never regex'd into (it may contain nested `BEGIN…END`,
 * quoted strings, comments, `;` and dollar-quote inner content, all opaque);
 * it ALSO has a structured parameter list (the only structured sub-shape).
 *
 * Captured shape (best-effort, engine-spanning):
 *   CREATE [OR REPLACE | OR ALTER] [EDITIONABLE|NONEDITIONABLE]
 *          [DEFINER = …] {FUNCTION|PROCEDURE|PROC} [schema.]name
 *          [ ( param[,…] ) ]
 *          [RETURNS|RETURN <returnsText>] [LANGUAGE <lang>]
 *          { AS|IS <body | $tag$…$tag$> | <BEGIN…> }
 *   ALTER {FUNCTION|PROCEDURE} name <…>   /   DROP {FUNCTION|PROCEDURE} name
 *
 * sqlite has no stored routines and never reaches this extractor.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice D7 — Procedure / Function shell UI;
 * now grammar-backed via DSE2 (VDE-2 Slice 4).
 */

import type { ParseOptions, StatementInfo } from '../../../sql-grammar/model';
import type { DdlStructure, ParsedBuffer } from '../../types';

import type { ParsedParkedRoutine } from './routineTypes';
import {
  consumePrefixes,
  consumeWord,
  findMatchingParenLocal,
  matchAsBody,
  matchBeginBody,
  matchDollarBody,
  matchLanguage,
  matchReturnsText,
  parseParamList,
  readRoutineName,
  skipSpacesLocal,
  strTrim,
  trimRange,
  type PrefixState,
} from './routine.parse';

export type { ParsedParkedRoutine, ParsedParkedRoutineParam } from './routineTypes';

/** Default options for the test/string entry shell — sqlite accepts all
 *  three quoted-identifier styles (`[ ]`, backticks, `" "`), so an engine-
 *  agnostic parked statement parses regardless of source dialect. */
const STRING_SHELL_OPTIONS: ParseOptions = { engine: 'sqlite' };

/** True when the trimmed range begins with the (prefix, NOT word-bounded)
 *  keyword `kw` — faithful to the removed parser's
 *  `trimmed.toUpperCase().startsWith(kw)`. */
function startsWithKeyword(source: string, s: number, e: number, kw: string): boolean {
  if (s + kw.length > e) return false;
  return source.slice(s, s + kw.length).toUpperCase() === kw;
}

/** Shared pure core. Operates on a byte range; returns the seed contract or
 *  null for non-routine / structurally-incomplete input. Mirrors the removed
 *  parser's `parseParkedRoutineStatement` + `parseCreateBody` exactly.
 *
 *  No `ParseOptions` arg — like the TRIGGER core this scan is genuinely
 *  engine-agnostic: the removed regex parser accepted every quote style and
 *  comments were never special, so the seed is identical regardless of
 *  source dialect. The public entry functions still take `options` for
 *  contract parity with the siblings. */
function parseRoutineCore(
  source: string,
  rangeStart: number,
  rangeEnd: number,
): ParsedParkedRoutine | null {
  const { s: start, e: end } = trimRange(source, rangeStart, rangeEnd);

  let action: 'create' | 'alter' | 'drop';
  let cursor: number;
  if (startsWithKeyword(source, start, end, 'CREATE')) { action = 'create'; cursor = start + 'CREATE'.length; }
  else if (startsWithKeyword(source, start, end, 'ALTER')) { action = 'alter'; cursor = start + 'ALTER'.length; }
  else if (startsWithKeyword(source, start, end, 'DROP')) { action = 'drop'; cursor = start + 'DROP'.length; }
  else return null;

  let prefix: PrefixState = {
    cursor,
    orReplace: false,
    mssqlOrAlter: false,
    oracleEditionable: false,
    oracleNonEditionable: false,
  };
  if (action === 'create') prefix = consumePrefixes(source, cursor, end);
  cursor = skipSpacesLocal(source, prefix.cursor, end);

  let kind: ParsedParkedRoutine['kind'];
  let k = consumeWord(source, cursor, end, 'PROCEDURE');
  if (k !== -1) { kind = 'procedure'; cursor = k; }
  else if ((k = consumeWord(source, cursor, end, 'PROC')) !== -1) { kind = 'procedure'; cursor = k; }
  else if ((k = consumeWord(source, cursor, end, 'FUNCTION')) !== -1) { kind = 'function'; cursor = k; }
  else return null;
  cursor = skipSpacesLocal(source, cursor, end);

  const nameRead = readRoutineName(source, cursor, end);
  if (!nameRead) return null;
  const name = nameRead.name;
  cursor = nameRead.end;

  if (action !== 'create') {
    return {
      action,
      kind,
      name,
      ...(prefix.orReplace ? { orReplace: true } : {}),
      ...(prefix.mssqlOrAlter ? { mssqlOrAlter: true } : {}),
    };
  }

  return parseCreateBody(source, cursor, end, kind, name, prefix);
}

/** Parse the CREATE-routine tail (parameter list / RETURNS / LANGUAGE /
 *  body). `restStart` is the index just past the routine name within the
 *  trimmed range; mirrors the removed parser's `parseCreateBody`. */
function parseCreateBody(
  source: string,
  restStart: number,
  end: number,
  kind: ParsedParkedRoutine['kind'],
  name: ParsedParkedRoutine['name'],
  prefix: PrefixState,
): ParsedParkedRoutine {
  let cursor = skipSpacesLocal(source, restStart, end);
  let params: ParsedParkedRoutine['params'];
  if (cursor < end && source[cursor] === '(') {
    const close = findMatchingParenLocal(source, cursor, end);
    if (close !== -1) {
      params = parseParamList(source.slice(cursor + 1, close));
      cursor = close + 1;
    }
  }
  cursor = skipSpacesLocal(source, cursor, end);

  // `tail = trimmed.slice(cursor)` — here the absolute span [tailStart, end).
  const tailStart = cursor;
  let returnsText: string | undefined;
  let bodyText: string | undefined;
  let pgsqlLanguage: string | undefined;

  const rm = matchReturnsText(source, tailStart, end);
  if (rm !== null) returnsText = strTrim(rm);

  const lm = matchLanguage(source, tailStart, end);
  if (lm !== null) pgsqlLanguage = lm;

  // Body precedence: first a word-position `AS`/`IS` + trailing body (with
  // the pgsql `$tag$ … $tag$` wrapper stripped when present), else a word-
  // bounded `BEGIN` to end. Body is the OPAQUE trimmed remainder — never
  // parsed (nested BEGIN…END / `;` / comments / dollar-inner all opaque).
  const asBody = matchAsBody(source, tailStart, end);
  if (asBody !== null) {
    let body = strTrim(asBody);
    const dollarInner = matchDollarBody(body);
    if (dollarInner !== null) body = strTrim(dollarInner);
    bodyText = body;
  } else {
    const beginBody = matchBeginBody(source, tailStart, end);
    if (beginBody !== null) bodyText = strTrim(beginBody);
  }

  return {
    action: 'create',
    kind,
    name,
    ...(params && params.length > 0 ? { params } : {}),
    ...(returnsText ? { returnsText } : {}),
    ...(bodyText ? { bodyText } : {}),
    ...(pgsqlLanguage ? { pgsqlLanguage } : {}),
    ...(prefix.orReplace ? { orReplace: true } : {}),
    ...(prefix.mssqlOrAlter ? { mssqlOrAlter: true } : {}),
    ...(prefix.oracleEditionable ? { oracleEditionable: true } : {}),
    ...(prefix.oracleNonEditionable ? { oracleNonEditionable: true } : {}),
  };
}

/** Buffer-entry seed helper consumed by the inline `[Edit Routine]` popover.
 *  Returns the parked-routine seed or null on non-routine / malformed. */
export function extractParkedRoutine(
  buffer: ParsedBuffer,
  statementInfo: StatementInfo,
): ParsedParkedRoutine | null {
  const { source } = buffer.parsedDocument;
  return parseRoutineCore(
    source,
    statementInfo.range.startOffset,
    statementInfo.range.endOffset,
  );
}

/** Thin string-entry shell (tests + engine-agnostic callers). The `options`
 *  param is accepted for signature parity with the GRANT/TYPE string shells
 *  but the routine scan is engine-agnostic, so it does not affect the seed. */
export function extractParkedRoutineFromString(
  stmtText: string,
  options: ParseOptions = STRING_SHELL_OPTIONS,
): ParsedParkedRoutine | null {
  void options;
  return parseRoutineCore(stmtText, 0, stmtText.length);
}

/** DSE2 arm — `extractDdlStructure` dispatches CREATE / ALTER / DROP
 *  {FUNCTION|PROCEDURE} here. Action-discriminated kind (mirrors
 *  `extractGrant`/`extractTrigger`): `'alter'` → `alter-routine`, `'drop'` →
 *  `drop-routine`, else (`'create'` or `parsed === null`) → `create-routine`.
 *  Never throws. */
export function extractRoutine(
  buffer: ParsedBuffer,
  statementInfo: StatementInfo,
): DdlStructure {
  const parsed = extractParkedRoutine(buffer, statementInfo);
  const kind =
    parsed?.action === 'alter'
      ? 'alter-routine'
      : parsed?.action === 'drop'
        ? 'drop-routine'
        : 'create-routine';
  return { kind, range: statementInfo.range, parsed };
}
